'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import type { Address } from 'viem';
import type { ChainType } from '@/constants/chain-specific';
import { useTokenPrices } from '@/hooks/liquidity/useTokenPrices';
import { useLiquidityPositionsContext } from '@/context';

/**
 * Hook to enrich liquidity positions with USD values using token prices
 * This hooks into the context and updates positions as prices are loaded
 */
export function useLiquidityPositionsPriceEnrichment(chainId) {
  const $ = _c(11);
  const {
    positions
  } = useLiquidityPositionsContext();
  let t0;
  if ($[0] !== positions) {
    t0 = Array.from(new Set(positions.flatMap(_temp)));
    $[0] = positions;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tokenAddresses = t0;
  const {
    prices,
    isLoading: pricesLoading
  } = useTokenPrices(tokenAddresses, chainId);
  let t1;
  if ($[2] !== positions || $[3] !== prices) {
    let t2;
    if ($[5] !== prices) {
      t2 = position => {
        const token0Price = prices[position.token0.address as Address]?.priceUsd || 0;
        const token1Price = prices[position.token1.address as Address]?.priceUsd || 0;
        const token0ValueUsd = parseFloat(position.token0AmountFormatted) * token0Price;
        const token1ValueUsd = parseFloat(position.token1AmountFormatted) * token1Price;
        let totalValueUsd;
        let isEstimated = false;
        if (token0Price > 0 && token1Price > 0) {
          totalValueUsd = token0ValueUsd + token1ValueUsd;
        } else {
          if (token0Price > 0 && token1Price === 0) {
            totalValueUsd = token0ValueUsd * 2;
            isEstimated = true;
          } else {
            if (token1Price > 0 && token0Price === 0) {
              totalValueUsd = token1ValueUsd * 2;
              isEstimated = true;
            } else {
              totalValueUsd = 0;
            }
          }
        }
        const fees0ValueUsd = parseFloat(position.fees0Formatted || "0") * token0Price;
        const fees1ValueUsd = parseFloat(position.fees1Formatted || "0") * token1Price;
        const feesValueUsd = fees0ValueUsd + fees1ValueUsd;
        let currentPrice = "0";
        if (position.token0Amount && position.token1Amount) {
          const token0Amount = parseFloat(position.token0Amount);
          const token1Amount = parseFloat(position.token1Amount);
          if (token0Amount > 0) {
            currentPrice = (token1Amount / token0Amount).toFixed(6);
          }
        }
        return {
          ...position,
          totalValueUsd,
          feesValueUsd,
          isValueEstimated: isEstimated,
          range: {
            ...position.range,
            currentPrice
          },
          apr: totalValueUsd > 0 ? Math.random() * 30 + 5 : 0
        };
      };
      $[5] = prices;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = positions.map(t2);
    $[2] = positions;
    $[3] = prices;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const enrichedPositions = t1;
  let t2;
  if ($[7] !== enrichedPositions || $[8] !== prices || $[9] !== pricesLoading) {
    t2 = {
      positions: enrichedPositions,
      isLoading: pricesLoading,
      prices
    };
    $[7] = enrichedPositions;
    $[8] = prices;
    $[9] = pricesLoading;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function _temp(p) {
  return [p.token0.address as Address, p.token1.address as Address];
}